import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of Loyalty — plan §5.2. Originally 20 reducers identical, 0 divergent, with web
 * adding 4 (`programDetailsMap` cache + opt-out) and mobile 3.
 *
 * As of 2026-09-05 web adds NONE: mobile took the same `packages/core/loyalty` flow, so the
 * programme cache and its two reducers moved here. Mobile still adds its own 3.
 *
 * ⚠️ `loyaltyDetails` is DELIBERATELY NOT in `sharedInitialState` — it is the migration's
 * first differing default and each platform file supplies its own. `Omit` makes the compiler
 * enforce that. See the platform files for what each one sets and why.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. Reducers the apps
 * wrote with no `action` param keep that shape (no-payload creator).
 *
 * The `Slice` interface is LOCAL (not exported) in both apps, so the platform files keep it
 * unexported too; only this shared base is exported, and only for them to extend.
 */
export interface SharedSlice {
  isLoading: boolean,
  /**
   * Map of channelDefId to the program details fetched from `/program/fetch`. Populated on
   * channel selection; cached to avoid redundant API calls.
   *
   * ⚠️ Keyed by CHANNEL, not by provider, so an entry outlives a provider change — which is why
   * every read of it in `packages/core/loyalty` is paired with a provider capability check.
   */
  programDetailsMap: Record<string, any>,
  loyaltyProviders: any,
  loyaltyDetails: any,
  selectedLoyaltyProvider: any,
  saleWithRedemptionInfo: any,
  loyaltyPointsAdded: any,
  loyatyOtpSuccess: any,
  loyaltyCustomerModal: boolean,
  externalRefId: string
}

/** The 8 fields whose defaults agree. `loyaltyDetails` is per platform — see above. */
export const sharedInitialState: Omit<SharedSlice, "loyaltyDetails"> = {
  isLoading: false,
  programDetailsMap: {},
  loyaltyProviders: {},
  selectedLoyaltyProvider: {},
  saleWithRedemptionInfo: {},
  loyaltyPointsAdded: false,
  loyatyOtpSuccess: {},
  loyaltyCustomerModal: false,
  externalRefId: '',
};

/** Identical in both apps. */
export const sharedReducers = {
  getLoyaltyProviders: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  getLoyaltyProvidersSuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = false;
    state.loyaltyProviders = action.payload;
  },
  getLoyaltyDetails: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  // NB: `Object.assign` MUTATES the existing draft rather than replacing it. Both apps do
  // this; it matters because mobile seeds `loyaltyDetails` with fixture data, so the first
  // success MERGES INTO that fixture instead of replacing it. Preserved verbatim.
  getLoyaltyDetailsSuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    let payload = action.payload;
    state.isLoading = false;
    state.loyaltyDetails = Object.assign(state.loyaltyDetails, {
      [payload.providerId]: payload.data,
    });
  },
  // No `action` param in either app — RTK generates a no-payload creator. Keep it that way.
  resetLoyaltyProviders: (state: SharedSlice) => {
    state.loyaltyDetails = {};
  },
  selectedLoyaltyDetails: (state: SharedSlice, action: PayloadAction<any>) => {
    state.selectedLoyaltyProvider = action.payload;
  },
  redeemPoints: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  redeemPointsSuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = false;
    state.saleWithRedemptionInfo = action.payload;
  },
  verifyLoyaltyOtp: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  verifyLoyaltyOtpSuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = false;
    state.loyatyOtpSuccess = action.payload;
  },
  refundLoyaltyPoints: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  refundLoyaltySuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = false;
    state.loyaltyPointsAdded = false;
  },
  updateLoyaltySlice: (state: SharedSlice, action: PayloadAction<any>) => {
    return action.payload;
  },
  externalOfferVerify: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  customerOfferList: (state: SharedSlice, action: PayloadAction<any>) => {
    state.isLoading = true;
  },
  toggleLoyaltyModal: (state: SharedSlice, action: PayloadAction<any>) => {
   state.loyaltyCustomerModal = action.payload
  },
  unBlockExternalDiscount: (state: SharedSlice, action: PayloadAction<any>) => {
  },
  externalCouponRefId: (state: SharedSlice, action: PayloadAction<any>) => {
    state.externalRefId = action.payload
  },
  sendCustomerConsentOtp: (state: SharedSlice, action: PayloadAction<any>) => { },
  verifyCustomerConsentOtp: (state: SharedSlice, action: PayloadAction<any>) => { },
  // Moved here from index.desktop.ts on 2026-09-04/05 with `packages/core/loyalty`: the adapter is
  // platform-neutral, so every action it dispatches has to exist in BOTH variants. Like the two
  // consent actions above them these are pure saga triggers with empty bodies.
  customerOptOut: (state: SharedSlice, action: PayloadAction<any>) => { },
  getProgramDetails: (state: SharedSlice, action: PayloadAction<any>) => { },
  // 🔴 `programDetailsMap` and these two reducers were web-only until 2026-09-05, when mobile
  // took the same flow. They cannot stay web-only: `canRedeemPointsOnPos` and
  // `isConsentMandatory` refuse a programme-publishing provider outright when the programme is
  // missing, so a mobile terminal on such a partner would have shown no redemption panel and no
  // consent block at all — a silent, permanent absence rather than an error.
  getProgramDetailsSuccess: (state: SharedSlice, action: PayloadAction<any>) => {
    const { channelDefId, data } = action.payload;
    state.programDetailsMap = { ...state.programDetailsMap, [channelDefId]: data };
  },
  // clears the entire programDetailsMap cache; dispatched on catalog refetch so stale program
  // details are re-fetched on next channel selection
  clearProgramDetails: (state: SharedSlice) => {
    state.programDetailsMap = {};
  },
};
